import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IoIosPerson, IoIosStar } from 'react-icons/io';
import { MdFavorite, MdFavoriteBorder } from 'react-icons/md';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { isThisISOWeek, isThisYear, isToday, isTomorrow } from 'date-fns';
import { isNil } from 'lodash-es';
import { useIsVisible } from 'react-is-visible';
import useMapView from '../../../hooks/useMapView';
import usePrelaunch from '../../../hooks/usePrelaunch';
import useTimezone from '../../../hooks/useTimezone';
import useUser from '../../../hooks/useUser';
import type { PartnerResultDescriptorType } from '../../../redux/ducks/SearchDucks.types';
import { getEnvironmentVariable } from '../../../utils/EnvironmentHelper/EnvironmentHelper';
import Spinner from '../Spinner/Spinner';

export type ProfessionalCardProps = {
  partnerDescriptor: PartnerResultDescriptorType;
  partnerIndex: number;
  onClick: () => void;
  onVisibilityChange: ({
    partnerId,
    partnerIndex,
    isVisible,
  }: {
    partnerId: string;
    partnerIndex: number;
    isVisible: boolean;
  }) => void;
};

const ProfessionalCard: React.FC<ProfessionalCardProps> = ({
  partnerDescriptor,
  partnerIndex,
  onClick,
  onVisibilityChange,
}) => {
  const { isLoggedIn } = useUser();
  const { isBeta } = usePrelaunch();
  const { t, i18n } = useTranslation();
  const nodeRef = useRef<any>(null);
  const mapView = useMapView();
  const isVisible = useIsVisible(nodeRef);
  const { currentTimezone } = useTimezone();

  const formatDay = useCallback((ts: number): string => {
    const targetDate = new Date(ts);

    if (isToday(targetDate)) {
      return t('static.generic.today');
    }

    if (isTomorrow(targetDate)) {
      return t('static.generic.tomorrow');
    }

    if (isThisISOWeek(targetDate)) {
      return t('static.generic.this_week_day', {
        name: Intl.DateTimeFormat(i18n.language, {
          weekday: 'long',
          timeZone: currentTimezone,
        }).format(targetDate),
      });
    }

    if (isThisYear(targetDate)) {
      return Intl.DateTimeFormat(i18n.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: currentTimezone,
      }).format(new Date(targetDate));
    }

    return Intl.DateTimeFormat(i18n.language, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      timeZone: currentTimezone,
    }).format(targetDate);
  }, []);

  useEffect(() => {
    if (isVisible) {
      onVisibilityChange({
        partnerId: partnerDescriptor.id,
        partnerIndex,
        isVisible: true,
      });
    } else {
      onVisibilityChange({
        partnerId: partnerDescriptor.id,
        partnerIndex,
        isVisible: false,
      });
    }
  }, [isVisible]);

  const partnerName = useMemo<string>(() => {
    const title =
      !isNil(partnerDescriptor?.title) &&
      !t(`static.generic.gender_${partnerDescriptor?.title}`).includes(
        'static.generic.gender_',
      )
        ? t(`static.generic.gender_${partnerDescriptor?.title}`)
        : '';

    return `${title} ${partnerDescriptor?.first_name} ${partnerDescriptor?.last_name}`.trim();
  }, [partnerDescriptor, t]);

  return (
    <button
      id={`partner-card-${partnerDescriptor.id}`}
      key={`partner-card-${partnerDescriptor.id}`}
      aria-label={partnerName}
      data-test-id={`partner-card-${partnerDescriptor.id}`}
      data-umami-event="partner-card-clicked"
      data-umami-event-partner={`partner-${partnerDescriptor.id}`}
      data-umami-event-label={`${partnerDescriptor.first_name} ${partnerDescriptor.last_name}`}
      className="group relative flex cursor-pointer flex-col items-center justify-center min-w-full"
      ref={nodeRef}
      onClick={onClick}
      type="button"
    >
      <div
        className={`aspect-square min-w-full ${
          mapView.isOpen && 'hidden pl-1 md:mt-2'
        } md:block`}
      >
        <div className="relative flex min-h-full min-w-full flex-col items-center justify-center rounded-2xl border border-slate-200 bg-slate-100 text-center group-hover:shadow">
          {partnerDescriptor?.media?.profile_picture ? (
            <div className="absolute z-0 flex min-h-full min-w-full flex-col items-center justify-center">
              <Spinner color="fill-sky-600" />
            </div>
          ) : (
            <IoIosPerson
              size={200}
              className="absolute max-h-full max-w-full text-slate-200"
            />
          )}
          {partnerDescriptor?.media?.profile_picture ? (
            <LazyLoadImage
              alt={`${partnerDescriptor.first_name} ${partnerDescriptor.last_name}`}
              style={{ objectFit: 'cover', height: '100%', width: '100%' }}
              src={`//${getEnvironmentVariable('REACT_APP_CDN_API')}${
                partnerDescriptor.media.profile_picture
              }`}
              className="z-10 rounded-2xl"
              effect="blur"
            />
          ) : (
            <div className="z-10 min-h-full min-w-full rounded-2xl">&nbsp;</div>
          )}
          {isLoggedIn && isBeta && (
            <span className="group absolute z-10">
              <span className="right-0 top-0 flex flex-col items-center justify-center p-3">
                <MdFavorite size={24} className="text-black opacity-50" />
              </span>
              <span className="right-0 top-0 flex flex-col items-center justify-center p-3">
                <MdFavoriteBorder
                  size={24}
                  className="text-white opacity-100"
                />
              </span>
            </span>
          )}
        </div>
      </div>
      <div className="relative my-4 flex min-h-24 min-w-full grow flex-col items-start justify-start text-start capitalize">
        <span className="flex min-w-full flex-row">
          <span className="line-clamp-1 min-h-full grow overflow-hidden text-ellipsis break-all font-semibold">
            {partnerName}
          </span>
          <span className="flex min-h-full flex-col items-end justify-end">
            {partnerDescriptor.reviews > 0 ? (
              <span className="flex min-h-full flex-row items-end justify-end">
                <span className="ml-0.5 flex min-h-full flex-col items-center justify-center text-sm">
                  <IoIosStar size={14} />
                </span>
                <span className="ml-0.5 flex min-h-full flex-col items-center justify-center text-sm">
                  {(partnerDescriptor.rating * 5).toFixed(2)}
                </span>
              </span>
            ) : (
              <span>&nbsp;</span>
            )}
          </span>
        </span>
        {partnerDescriptor?.first_appointment_available ? (
          <span className="line-clamp-1 overflow-hidden text-ellipsis break-all text-sm capitalize text-slate-500">
            {t('static.generic.next_available')}:{' '}
            {formatDay(partnerDescriptor.first_appointment_available)}
            {', '}
            {Intl.DateTimeFormat(i18n.language, {
              hour: 'numeric',
              minute: 'numeric',
              hour12: false,
              timeZone: currentTimezone,
            }).format(new Date(partnerDescriptor.first_appointment_available))}
          </span>
        ) : (
          <span className="line-clamp-1 overflow-hidden text-ellipsis break-all text-sm text-slate-500">
            {t('static.generic.not_available')}
          </span>
        )}
        <span className="line-clamp-1 overflow-hidden text-ellipsis break-all text-sm text-slate-500">
          {partnerDescriptor.location.address},{' '}
          {partnerDescriptor.location.city}
        </span>
        <span className="mt-2 line-clamp-1 overflow-hidden text-ellipsis break-all text-sm">
          <span className="font-bold">
            {new Intl.NumberFormat(i18n.language, {
              style: 'currency',
              currency: partnerDescriptor.service.currency,
            }).format(partnerDescriptor.service.price)}
          </span>{' '}
          <span>{partnerDescriptor.service.name}</span>
        </span>
      </div>
    </button>
  );
};

export default ProfessionalCard;
